import { useCallback, useEffect, useState } from "react";
import { Plus, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/components/Share";
import ProfileForm from "@/sell/ProfileForm";
import PlanCard from "@/sell/PlanCard";
import MyListings from "@/sell/MyListings";
import ListingEditor from "@/sell/ListingEditor";
import Inbox from "@/sell/Inbox";

function SellIntro({ login }) {
  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20" data-testid="sell-intro">
      <p className="eyebrow rise">Sell on the market</p>
      <h1 className="rise d1 mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1a261f]">Reach New Zealand's sun lovers.</h1>
      <p className="rise d2 mt-6 inline-block bg-[#eef3ee] text-[#1f402b] px-4 py-2" data-testid="sell-free-banner"><strong>FREE to list until 1 January 2027.</strong> Sign up now to get your adverts in front of visitors early and earn a permanent Founding seller badge.</p>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {[["Free, then NZ$5 a month", "Nothing to pay until 1 January 2027. After that, $5 a month in advance, locked in for 12 months."],
          ["28 adverts a month", "1 credit = 1 advert, live for 30 days. Credits reset each month."],
          ["Cancel any time", "Cancel before 1 January 2027 and you're never charged. After that, it takes effect at the end of your paid month."]].map(([t, d]) => (
          <div key={t} className="border border-[#e2dcd4] bg-white p-6"><p className="font-display text-2xl text-[#1f402b]">{t}</p><p className="mt-2 text-sm text-[#5a665e] leading-relaxed">{d}</p></div>
        ))}
      </div>
      <p className="mt-8 text-sm text-[#5a665e] max-w-2xl">To keep every seller genuine, we check your card when you sign up. Nothing is charged during the free period. Sun care, beach gear, stays, experiences and wellness only: things naturists and sun lovers actually use. Nothing sexual or adult. Every advert is checked before it goes live.</p>
      <button onClick={() => login("/sell")} className="mt-8 bg-[#1f402b] text-white px-7 py-3.5 text-sm hover:bg-[#2c5a3d]" data-testid="sell-login-button">Sign in with Google to start</button>
    </div>
  );
}

export default function Sell() {
  const { user, loading, login, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [listings, setListings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null);
  usePageMeta("Sell on the market", "Sell sun-lover products and services for NZ$5 a month.");

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/seller/me"), api.get("/seller/listings"), api.get("/seller/messages")]);
    setMe(a.data);
    setListings(b.data);
    setMessages(c.data);
  }, []);

  useEffect(() => {
    if (user) load().catch(() => {});
  }, [user, load]);

  if (loading) return null;
  if (!user) return <SellIntro login={login} />;
  if (!me) return <p className="max-w-7xl mx-auto px-8 pt-20 text-sm text-[#5a665e]">Loading…</p>;
  const seller = me.seller;
  const unread = messages.filter((m) => !m.read).length;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14" data-testid="seller-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Seller page</p>
          <h1 className="mt-3 text-4xl sm:text-5xl tracking-tight text-[#1a261f]">{seller?.business_name || "Set up your shop"}</h1>
        </div>
        <button onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-[#5a665e]" data-testid="seller-logout"><LogOut size={14} /> Sign out ({me.user.email})</button>
      </div>
      <div className="mt-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-6">
          <PlanCard seller={seller} freeUntil={me.free_until} onChange={() => load()} />
          <ProfileForm seller={seller} onSaved={() => load()} />
        </div>
        <div className="lg:col-span-7 space-y-10">
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl text-[#1a261f]">Your adverts</h2>
              <button disabled={!seller?.plan_active || seller.credits < 1} onClick={() => setEditing({})} data-testid="seller-new-listing-button"
                className="inline-flex items-center gap-2 bg-[#1f402b] text-white px-4 py-2.5 text-sm hover:bg-[#2c5a3d] disabled:opacity-40">
                <Plus size={15} /> New advert
              </button>
            </div>
            {!seller?.plan_active && <p className="mb-4 text-sm text-[#5a665e]" data-testid="seller-needs-plan">Subscribe to start posting adverts.</p>}
            <MyListings listings={listings} onEdit={setEditing} reload={load} />
          </section>
          <section>
            <h2 className="text-2xl text-[#1a261f] mb-4">Messages {unread > 0 && <span className="ml-2 font-mono-k text-xs bg-[#c5903b] text-white px-2 py-0.5" data-testid="seller-unread-count">{unread} new</span>}</h2>
            <Inbox messages={messages} reload={load} />
          </section>
        </div>
      </div>
      {editing && <ListingEditor listing={editing.id ? editing : null} categories={me.categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
